import { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react'
import axios from 'axios'
import { IMMEDIATE_ACTION, PROCESS_ACTIVITY_STATUSES } from '../../src/utility/constants'
import { useAuthContext } from '../hooks/useAuthContext'
import { processTemplateService } from '../services/processTemplateService'
import { userTrackingStorage, myTasksTrackingStorage } from '../../src/utility/storageUtils'

const MyTasksContext = createContext(null)

export function useMyTasks() {
  const ctx = useContext(MyTasksContext)
  if (!ctx) throw new Error('useMyTasks must be used inside <MyTasksProvider>')
  return ctx
}

export function MyTasksProvider({ children }) {
  const { user } = useAuthContext()
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

  const [allTask, setAllTask] = useState([])
  const [visibleData, setVisibleData] = useState([])
  const [hiddenTaskIds, setHiddenTaskIds] = useState(new Set())
  const [filterCategories, setFilterCategories] = useState([])
  const [allProcessRefNums, setAllProcessRefNums] = useState([])

  const [allCategories, setAllCategories] = useState([])
  const [categoriesFetched, setCategoriesFetched] = useState(false)

  const [isActiveTasks, setIsActiveTasks] = useState(true)
  const [isCardView, setIsCardView] = useState(true)

  const [newTasksAvailable, setNewTasksAvailable] = useState(0)
  const [pendingNewTasks, setPendingNewTasks] = useState([])

  const [filter, setFilter] = useState({
    categoryId: null,
    processTemplateId: null,
    activityTemplateId: null,
  })
  const [selectedRefNums, setSelectedRefNums] = useState([])
  const [actionRequired, setActionRequired] = useState(IMMEDIATE_ACTION.immediate_action_required)
  const [prefsHydrated, setPrefsHydrated] = useState(false)

  const [nextCursor, setNextCursor] = useState(null)
  const [hasMore, setHasMore] = useState(true)

  const scrollPositionRef = useRef(0)
  const [fetchedFor, setFetchedFor] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const abortRef = useRef(null)

  const allTaskRef = useRef(allTask)
  const isActiveTasksRef = useRef(isActiveTasks)
  const isCardViewRef = useRef(isCardView)
  const initialLoadingRef = useRef(initialLoading)
  const prevUserTokenRef = useRef(undefined)

  useEffect(() => {
    if (!user?.id || prefsHydrated) return
    const savedUser = userTrackingStorage.getForUser(user.id)
    const savedMyTasks = myTasksTrackingStorage.getForUser(user.id)

    if (savedUser?.filters) setFilter(savedUser.filters)
    if (savedUser?.selectedRefNums) setSelectedRefNums(savedUser.selectedRefNums)
    if (savedMyTasks?.isActiveTasks !== undefined) setIsActiveTasks(savedMyTasks.isActiveTasks)
    if (savedMyTasks?.isCardView !== undefined) setIsCardView(savedMyTasks.isCardView)
    if (savedMyTasks?.actionRequired !== undefined) setActionRequired(savedMyTasks.actionRequired)
    setPrefsHydrated(true)
  }, [user?.id, prefsHydrated])

  useEffect(() => {
    if (!user?.id || !prefsHydrated) return
    userTrackingStorage.setForUser(user.id, { filters: filter, selectedRefNums })
  }, [user?.id, filter, selectedRefNums, prefsHydrated])

  useEffect(() => {
    if (!user?.id || !prefsHydrated) return
    myTasksTrackingStorage.setForUser(user.id, { isActiveTasks, isCardView, actionRequired })
  }, [user?.id, isActiveTasks, isCardView, actionRequired, prefsHydrated])

  useEffect(() => {
    allTaskRef.current = allTask
  }, [allTask])

  useEffect(() => {
    isActiveTasksRef.current = isActiveTasks
  }, [isActiveTasks])

  useEffect(() => {
    isCardViewRef.current = isCardView
  }, [isCardView])

  useEffect(() => {
    initialLoadingRef.current = initialLoading
  }, [initialLoading])

  const getAxiosOptions = useCallback(
    (extra = {}) =>
      user ? { headers: { Authorization: `Bearer ${user.token}` }, ...extra } : extra,
    [user?.token],
  )

  const setFilterAndResetRefs = useCallback((newFilter) => {
    setSelectedRefNums([])
    setFilter(newFilter)
  }, [])

  const setSelectedRefNumsAndPersist = useCallback((value) => {
    setSelectedRefNums(value)
  }, [])

  const resetAllState = useCallback(() => {
    setAllTask([])
    setVisibleData([])
    setHiddenTaskIds(new Set())
    setFilterCategories([])
    setAllProcessRefNums([])
    setAllCategories([])
    setCategoriesFetched(false)
    setNewTasksAvailable(0)
    setPendingNewTasks([])
    setNextCursor(null)
    setHasMore(true)
    setFetchedFor(null)
    setInitialLoading(true)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    const currentToken = user?.token ?? null
    if (prevUserTokenRef.current === undefined) {
      prevUserTokenRef.current = currentToken
      return
    }
    if (prevUserTokenRef.current === currentToken) return
    prevUserTokenRef.current = currentToken

    if (currentToken === null) {
      // Actual logout — full reset including preferences
      resetAllState()
      setFilter({ categoryId: null, processTemplateId: null, activityTemplateId: null })
      setSelectedRefNums([])
      setActionRequired(IMMEDIATE_ACTION.immediate_action_required)
      setPrefsHydrated(false)
    } else {
      // Token rotation / re-auth — only reset fetch state, keep preferences
      setAllTask([])
      setVisibleData([])
      setNextCursor(null)
      setHasMore(true)
      setFetchedFor(null)
      setInitialLoading(true)
      setIsLoading(false)
    }
  }, [user?.token, resetAllState])

  const ensureCategories = useCallback(async () => {
    if (categoriesFetched) return
    try {
      const response = await processTemplateService.getProcessForCategory(user)
      const result = response.data
      const categoryMap = new Map()
      result?.processCategories?.forEach((cur) => {
        if (!categoryMap.has(cur.process_category_id)) {
          categoryMap.set(cur.process_category_id, {
            id: cur.process_category_id,
            name: cur.name,
          })
        }
      })

      setAllCategories([...categoryMap.values()])
      setCategoriesFetched(true)
    } catch (error) {
      console.error('Error fetching processes:', error)
    }
  }, [categoriesFetched, user])

  // Core fetch
  const fetchData = useCallback(
    async (
      cursor = null,
      loadRefNums = false,
      overrideActive = isActiveTasks,
      overrideCard = isCardView,
      overrideFilter = filter,
      overrideRefNums = selectedRefNums,
      skipGuard = false,
    ) => {
      if (
        !skipGuard &&
        isLoading &&
        cursor === null &&
        fetchedFor?.activeTasks === overrideActive &&
        fetchedFor?.cardView === overrideCard
      )
        return

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setIsLoading(true)

      const processActivityStatus = overrideActive
        ? PROCESS_ACTIVITY_STATUSES.ACTIVE
        : PROCESS_ACTIVITY_STATUSES.CLOSED

      const searchQueryReference =
        overrideRefNums?.length > 0 ? overrideRefNums.map((ref) => ref?.label ?? ref) : []
      const searchQuery = `&search=${searchQueryReference.join(',')}`
      const encodedFilter = encodeURIComponent(JSON.stringify(overrideFilter))

      const apiUrl = overrideCard
        ? !overrideActive
          ? `${NODE_BASE_URL}/data-entry/process-data/assigned-process-data/${processActivityStatus}?limit=100${
              cursor ? `&cursor=${cursor}` : ''
            }${searchQuery}&filter=${encodedFilter}`
          : `${NODE_BASE_URL}/data-entry/process-data/assigned-process-data/${processActivityStatus}`
        : `${NODE_BASE_URL}/data-entry/process-data/assigned-tabular-process-data/${processActivityStatus}`

      try {
        const resp = await axios.get(apiUrl, {
          ...getAxiosOptions(),
          signal: controller.signal,
        })
        if (controller.signal.aborted) return

        const result = resp.data

        const hiddenIdsSet = new Set()

        if (Array.isArray(result.finalData)) {
          for (const item of result.finalData) {
            if (item.is_tasks_hidden === true) {
              hiddenIdsSet.add(item.id)
            }
          }
        }

        setHiddenTaskIds(hiddenIdsSet)

        if (overrideCard) {
          const incoming = Array.isArray(result?.data) ? result.data : []
          const filteredIncoming = incoming.filter((proc) => !hiddenIdsSet.has(proc.process_id))

          if (overrideActive) {
            setNextCursor(null)
            setHasMore(false)
          } else {
            setNextCursor(result.nextCursor ?? null)
            setHasMore(Boolean(result.nextCursor))
          }

          if (Array.isArray(result?.finalData)) {
            const finalDataMap = new Map(result.finalData.map((fd) => [fd.id, fd]))

            const matched = []

            for (const proc of incoming) {
              const processData = finalDataMap.get(proc.process_id)

              if (processData) {
                // Build lookup map from all_field_templates
                const fieldTemplateMap = new Map(
                  processData.all_field_templates.map((field) => [
                    field.field_template_name,
                    field,
                  ]),
                )

                const enrichedExtractedValues = processData.extracted_values.map((value) => {
                  const fieldTemplate = fieldTemplateMap.get(value.key)

                  return {
                    ...value,
                    dataStructure: fieldTemplate?.data_structure || null,
                  }
                })

                matched.push({
                  process_id: proc.process_id,
                  extracted_values: enrichedExtractedValues,
                })
              }
            }

            setVisibleData(matched)
          } else {
            setVisibleData([])
          }

          setAllTask((prev) => {
            const merged = cursor ? [...prev, ...filteredIncoming] : filteredIncoming
            return Array.from(new Map(merged.map((t) => [t.process_id, t])).values())
          })

          setFilterCategories(result?.allCategories || [])
          if (loadRefNums && result.allProcessRefNums) {
            setAllProcessRefNums(result.allProcessRefNums)
          }
        } else {
          const incoming = Array.isArray(result) ? result : []
          const filteredIncoming = incoming.filter((proc) => !hiddenIdsSet.has(proc.process_id))
          setVisibleData([])
          setAllTask(filteredIncoming)
          setFilterCategories(Array.isArray(result?.allCategories) ? result.allCategories : [])
          setNextCursor(null)
          setHasMore(false)
        }

        setFetchedFor({ activeTasks: overrideActive, cardView: overrideCard })
      } catch (error) {
        if (axios.isCancel?.(error) || error.name === 'CanceledError') return
        console.error('Fetch error:', error)
      } finally {
        if (abortRef.current === controller) abortRef.current = null
      }

      setInitialLoading(false)
      setIsLoading(false)
    },
    [getAxiosOptions, NODE_BASE_URL, isLoading, fetchedFor, filter, selectedRefNums],
  )

  const ensureData = useCallback(
    (overrideActive = isActiveTasks, overrideCard = isCardView) => {
      const cacheHit =
        fetchedFor &&
        fetchedFor.activeTasks === overrideActive &&
        fetchedFor.cardView === overrideCard &&
        allTask.length > 0

      if (cacheHit) {
        setInitialLoading(false)
        setIsLoading(false)
        return true
      }
      return false
    },
    [fetchedFor, allTask.length, isActiveTasks, isCardView],
  )

  // Silent background fetch
  const fetchSilently = useCallback(async () => {
    if (!user) return
    if (initialLoadingRef.current) return

    const apiUrl = `${NODE_BASE_URL}/data-entry/process-data/assigned-process-data/${PROCESS_ACTIVITY_STATUSES.ACTIVE}`

    try {
      const resp = await axios.get(apiUrl, getAxiosOptions())
      const result = resp.data
      const incoming = Array.isArray(result?.data) ? result.data : []
      const incomingIds = new Set(incoming.map((t) => t.process_id))
      const currentIds = new Set(allTaskRef.current.map((t) => t.process_id))
      const brandNew = incoming.filter((t) => !currentIds.has(t.process_id))

      if (isActiveTasksRef.current && isCardViewRef.current) {
        setAllTask((prev) => {
          const stillValid = prev.filter((t) => incomingIds.has(t.process_id))
          const merged = [...brandNew, ...stillValid]
          return Array.from(new Map(merged.map((t) => [t.process_id, t])).values())
        })
      }

      if (brandNew.length > 0) {
        setNewTasksAvailable(brandNew.length)
        setPendingNewTasks(brandNew)
      }
    } catch (err) {
      if (axios.isCancel?.(err) || err.name === 'CanceledError') return
      console.error('Silent poll error:', err)
    }
  }, [user, getAxiosOptions, NODE_BASE_URL])

  const POLL_INTERVAL_MS = 5 * 60 * 1000

  useEffect(() => {
    if (!user) return
    const id = setInterval(fetchSilently, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [user, fetchSilently])

  useEffect(() => {
    setPendingNewTasks([])
    setNewTasksAvailable(0)
  }, [isActiveTasks, isCardView])

  const applyNewTasks = useCallback(() => {
    if (pendingNewTasks.length > 0) {
      setAllTask((prev) => {
        const merged = [...pendingNewTasks, ...prev]
        return Array.from(new Map(merged.map((t) => [t.process_id, t])).values())
      })
      setNewTasksAvailable(pendingNewTasks.length)
      setPendingNewTasks([])
    }
  }, [pendingNewTasks])

  useEffect(() => {
    if (newTasksAvailable === 0) return
    const timer = setTimeout(() => setNewTasksAvailable(0), 4000)
    return () => clearTimeout(timer)
  }, [newTasksAvailable])

  const switchActiveTasks = useCallback(
    (newValue) => {
      setIsActiveTasks(newValue)
      // Reset filters + reference numbers
      setFilterAndResetRefs({
        categoryId: null,
        processTemplateId: null,
        activityTemplateId: null,
      })
      setActionRequired(IMMEDIATE_ACTION.all)
      setAllTask([])
      setNextCursor(null)
      setHasMore(true)
      setFetchedFor(null)
      setInitialLoading(true)
    },
    [setFilterAndResetRefs],
  )

  const switchCardView = useCallback((newValue) => {
    setIsCardView(newValue)
    setAllTask([])
    setNextCursor(null)
    setHasMore(true)
    setFetchedFor(null)
    setInitialLoading(true)
  }, [])

  const resetForFilterChange = useCallback(() => {
    setAllTask([])
    setNextCursor(null)
    setHasMore(true)
    setFetchedFor(null)
    setInitialLoading(true)
  }, [])

  const markStale = useCallback(() => {
    setFetchedFor(null)
    setInitialLoading(true)
  }, [])

  const deleteTaskLocally = useCallback((processId) => {
    setAllTask((prev) => prev.filter((t) => t.process_id !== processId))
  }, [])

  const saveScrollPosition = useCallback(() => {
    scrollPositionRef.current = window.scrollY
  }, [])

  const restoreScrollPosition = useCallback(() => {
    const pos = scrollPositionRef.current
    if (pos > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: pos, behavior: 'instant' })
        })
      })
    }
  }, [])

  return (
    <MyTasksContext.Provider
      value={{
        allTask,
        setAllTask,
        visibleData,
        hiddenTaskIds,
        filterCategories,
        allProcessRefNums,
        allCategories,
        ensureCategories,
        isActiveTasks,
        isCardView,
        switchActiveTasks,
        switchCardView,
        filter,
        setFilter,
        setFilterAndResetRefs,
        selectedRefNums,
        setSelectedRefNums: setSelectedRefNumsAndPersist,
        actionRequired,
        setActionRequired,
        nextCursor,
        hasMore,
        initialLoading,
        isLoading,
        fetchData,
        ensureData,
        markStale,
        resetForFilterChange,
        deleteTaskLocally,
        saveScrollPosition,
        restoreScrollPosition,
        abortRef,
        newTasksAvailable,
        applyNewTasks,
      }}
    >
      {children}
    </MyTasksContext.Provider>
  )
}
